import { memo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useHealthcareServiceContext } from "@/contexts/HealthcareServiceContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { Form } from "@/pages/schedule/components/form/Form";

export interface AgendaProfessionalOption {
  label: string;
  value: string;
}

/**
 * Criar agenda de teleconsultoria SEM sair da página: reutiliza o Form do
 * /newschedule já no tipo "teleconsultoria" (passo "Tipo" escondido).
 * `presetProfessional`: profissional já escolhido (o próprio usuário ou,
 * na visão clínica, o profissional para quem o gestor cria).
 */
export const CreateAgendaDialog = memo(function CreateAgendaDialog({
  open,
  presetProfessional,
  onClose,
  onCreated,
}: {
  open: boolean;
  presetProfessional?: AgendaProfessionalOption;
  onClose: () => void;
  onCreated: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { refetchLocation } = useLocationContext();
  const { refetchHealthcareService } = useHealthcareServiceContext();

  // Igual à página /newschedule: dados frescos para reutilizar a Location de teleatendimento.
  useEffect(() => {
    if (!open) return;
    refetchLocation();
    refetchHealthcareService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack>
          <Typography variant="h6" fontWeight={700} component="span">
            Criar agenda de teleconsultoria
          </Typography>
          {presetProfessional?.label && (
            <Typography variant="body2" color="text.secondary" component="span">
              {presetProfessional.label}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} aria-label="Fechar" sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Só monta aberto: cada abertura começa um formulário novo. */}
        {open && (
          <Form
            presetAttendanceType="teleconsultoria"
            presetProfessional={presetProfessional}
            onCreated={() => {
              onCreated();
              onClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
});
